import { NumberInput as MantineNumberInput } from "@mantine/core";
import classnames from "classnames";
import { componentWrapper } from "../../utilities";
import type {
    CustomNumberInputFormikFactory,
    CustomNumberInputFactory,
    INumberInputFormikProps,
    IProps,
} from "./number-input.types";
import { useField, useFormikContext } from "formik";
import inputStyles from "../input/input.module.scss";
import { Icon } from "../icon";

/**
 * Number input component for numeric value entry.
 * @param {IconDefinition} props.icon - Optional icon to display on the left side of the input.
 * @param {object} props.classNames - Custom class names combined with the default slot classes.
 * @returns {JSX.Element} The NumberInput component.
 */
const NumberInput = componentWrapper<CustomNumberInputFactory, IProps>(
    "NumberInput",
    ({ icon, classNames, ...otherProps }, ref) => {
        return (
            <MantineNumberInput
                ref={ref}
                // Had to add this because Mantine internally adds the sm size to the text input label
                labelProps={{ style: { "--input-label-size": undefined } }}
                classNames={{
                    root: classnames(
                        inputStyles["wrapperRoot"],
                        typeof classNames === "object" ? classNames?.root : ""
                    ),
                    label: classnames(inputStyles["label"], typeof classNames === "object" ? classNames?.label : ""),
                    input: classnames(inputStyles["input"], typeof classNames === "object" ? classNames?.input : ""),
                    wrapper: classnames(
                        inputStyles["inputWrapper"],
                        typeof classNames === "object" ? classNames?.wrapper : ""
                    ),
                    section: classnames(
                        inputStyles["section"],
                        typeof classNames === "object" ? classNames?.section : ""
                    ),
                }}
                leftSection={icon && <Icon icon={icon} />}
                {...otherProps}
            />
        );
    }
);

/**
 * Number input component integrated with Formik for form handling and validation.
 * @param {string} props.name - The name of the number input field for Formik.
 * @param {function} props.validate - Custom validation function for the field.
 * @returns {JSX.Element} The NumberInputFormik component.
 */
const NumberInputFormik = componentWrapper<CustomNumberInputFormikFactory, INumberInputFormikProps>(
    "NumberInputFormik",
    ({ name, validate, hideError, ...otherProps }, ref) => {
        const { setFieldValue } = useFormikContext();
        const [{ onChange, ...field }, meta] = useField({ name, validate });

        return (
            <NumberInput
                ref={ref}
                {...otherProps}
                onChange={(value) => setFieldValue(name, value)}
                error={hideError ? undefined : meta.touched ? meta.error : undefined}
                {...field}
            />
        );
    }
);

NumberInput.Formik = NumberInputFormik;

export { NumberInput, NumberInputFormik };
